import * as React from "react";
import {
  Button,
  Dropdown,
  makeStyles,
  tokens,
  Option,
  Label,
  Card,
  Spinner,
  MessageBar,
  MessageBarBody,
  Text,
} from "@fluentui/react-components";
import { EmailResponseFormProps } from "../../shared/types";
import { useGenerateResponse } from "../../shared/hooks/useGenerateResponse";
import { useOptionalDatasources } from "../../shared/hooks/useOptionalDatasources";
import {FeedbackButtons} from "../../shared/components/FeedbackButtons";
import {FeedbackContext} from "../../shared/types/feedback";
import {getAgentId} from "../../shared/utils/config";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px 25px",
  },
  introCard: {
    marginBottom: "30px",
    padding: "15px",
    width: "100%",
  },
  cardTitle: {
    fontSize: tokens.fontSizeBase500,
    marginBottom: "12px",
  },
  bulletList: {
    margin: "0",
    paddingLeft: "20px",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  bulletItem: {
    marginBottom: "8px",
    "&:last-child": {
      marginBottom: "0",
    },
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%",
    maxWidth: "400px",
  },
  disclaimerContainer: {
    marginBottom: "8px",
  },
  disclaimerTitle: {
    display: "block",
    marginBottom: "4px",
  },
  disclaimerText: {
    color: tokens.colorNeutralForeground2,
  },
  dropdownContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  errorMessage: {
    marginTop: "12px",
    width: "100%",
  },
  buttonContent: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
});

export const EmailResponseForm: React.FC<EmailResponseFormProps> = ({ insertText }) => {
  const styles = useStyles();
  const { options, defaultOption, isLoading: isLoadingOptions } = useOptionalDatasources();
  const [selectedAssistant, setSelectedAssistant] = React.useState<string>("");
  const { mutate, isPending, isError, reset } = useGenerateResponse();
  const [lastResponseInfo, setLastResponseInfo] = React.useState<{
    chatId: string;
    chatMessageId: string;
    agentId: string;
  } | null>(null);

  React.useEffect(() => {
    if (!isLoadingOptions && defaultOption && !selectedAssistant) {
      setSelectedAssistant(defaultOption.dataSourceId);
    }
  }, [isLoadingOptions, defaultOption, selectedAssistant]);

  const selectedOption = options.find((o) => o.dataSourceId === selectedAssistant);
  const displayValue = selectedOption?.label || selectedAssistant;


  const handleInsertResponse = () => {
    reset();
    const fixedAgentId = getAgentId()
    mutate(
      {
        assistant: selectedAssistant,
        agentId: fixedAgentId,
        source: "OUTLOOK",
        selectedCase: null,
      },
        {
          onSuccess: async (response) => {
            await insertText(response.choices[0].message.content);

            setLastResponseInfo({
              chatId: response.chatId,
              chatMessageId: response.assistantMessageId,
              agentId: fixedAgentId,
            });
          },
        }
    );
  };

  const handleAssistantChange = (_event: unknown, data: { optionValue?: string }) => {
    if (data.optionValue) {
      setSelectedAssistant(data.optionValue);
    }
  };
  return (
    <div className={styles.root}>
      <Card className={styles.introCard}>
        <div className={styles.cardTitle}>
          Der Poloplast Support Assistent kann technische E-Mails automatisiert beantworten.
        </div>
        <ul className={styles.bulletList}>
          <li className={styles.bulletItem}>
            Bezieht Wissen aus den technischen Handbüchern für Österreich und Deutschland
          </li>
          <li className={styles.bulletItem}>
            Formuliert ansprechende Antworten direkt für den Kunden
          </li>
          <li className={styles.bulletItem}>E-Mail-Verlauf wird mit einbezogen</li>
        </ul>
      </Card>
      <div className={styles.formContainer}>
        <div className={styles.disclaimerContainer}>
          <Text weight="semibold" className={styles.disclaimerTitle}>
            Wichtiger Hinweis: KI-generierter Entwurf – Bitte prüfen Sie die Antwort.
          </Text>
          <Text size={200} className={styles.disclaimerText}>
            <strong>Entwurf von KARLI.</strong> Bitte beachten Sie: Die Antworten werden von einer
            KI generiert und sollten vor dem Senden auf inhaltliche Richtigkeit (insbesondere
            technische Daten) und Rechtschreibung geprüft werden.
          </Text>
        </div>
        <div className={styles.dropdownContainer}>
          <Label htmlFor="assistant-dropdown">Region auswählen (Datenbasis)</Label>
          <Dropdown
            id="assistant-dropdown"
            value={displayValue}
            selectedOptions={[selectedAssistant]}
            onOptionSelect={handleAssistantChange}
            disabled={isPending || isLoadingOptions}
          >
            {options.map((option) => (
              <Option key={option.id} value={option.dataSourceId}>
                {option.label}
              </Option>
            ))}
          </Dropdown>
        </div>
        <Button
          appearance="primary"
          size="large"
          onClick={handleInsertResponse}
          disabled={!selectedAssistant || isPending || isLoadingOptions}
        >
          <span className={styles.buttonContent}>
            {isPending && <Spinner size="tiny" />}
            {isPending ? "Antwort wird generiert..." : "Entwurf mit KARLI erstellen"}
          </span>
        </Button>
        {lastResponseInfo && (
            <FeedbackButtons
                chatId={lastResponseInfo.chatId}
                chatMessageId={lastResponseInfo.chatMessageId}
                agentId={lastResponseInfo.agentId}
                context={FeedbackContext._ChatMessage}
            />
        )}
        {isError && (
          <MessageBar intent="error" className={styles.errorMessage}>
            <MessageBarBody>
              Leider kam es bei ihrer Anfrage zu einem Fehler. Bitte versuchen Sie es später noch
              einmal.
            </MessageBarBody>
          </MessageBar>
        )}
      </div>
    </div>
  );
};

export default EmailResponseForm;
